import { css } from '@emotion/react'
import styled from '@emotion/styled'
import dynamic from 'next/dynamic'
import { memo, useEffect, useRef, useState } from 'react'
import type { LayoutProps } from '../../../../types/mfc'
import { SecureStateType } from '../../../constants/enums'
import { isTouchDevice } from '../../../utilities/browser'
import { isIOS } from '../../../utilities/userAgent'
import { AppStateType, useAppStateSelector } from '../../Hooks/useAppState'
import { useHealthAppState } from '../../Hooks/useHealthAppState'
import { useLayoutSelector } from '../../Hooks/useLayout'
import { useFloatingRoomStateSelector } from '../../Hooks/useModal'
import { useModelsLoadedAppState } from '../../Hooks/useModelsLoadedAppState'
import { usePassive } from '../../Hooks/usePassive'
import { usePinnedRoomCount } from '../../Hooks/useRoom'
import { useRouteSelector } from '../../Hooks/useRoute'
import { useScreenStateSelector } from '../../Hooks/useScreenState'
import { useSecureState } from '../../Hooks/useSecureState'
import { useSidebarSelector } from '../../Hooks/useSidebar'
import { palette } from '../../styles/colors'
import AgeGateNotice from '../Authentication/AgeGateNotice'
import { hideDragHelper } from '../Chat/Rooms/PinnedRooms/PinDragHelper'
import LoadingScreen from '../LoadingScreen'
import Floaters from './Floaters'
import Header from './Header'
import Modals from './Modals'
import Nav from './Nav'

// just delay the loading instead of dynamic loading it, so when the user tries
// to open menu or go to a room the component is there already while still not
// being included in the main
const pinsPromise = import('../Chat/Rooms/PinnedRooms/PinContainer')
const PinContainer = dynamic(() => pinsPromise)
const sidebarPromise = import('../Sidebar')
const Sidebar = dynamic(() => sidebarPromise)

const Maintenance = dynamic(() => import('../Maintenance'))

const sharedStyles = {
    display: 'flex',
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    overflow: 'hidden',
} as const

const StyledLoadingScreenContainer = styled('div')({
    zIndex: '99999999999 !important',
    position: 'fixed',
    height: '100%',
    width: '100%',
    background: palette.background,
})

const StyledDivRoot = styled('div')({
    ...sharedStyles,
})
const StyledChildrenContainer = styled('div')({
    ...sharedStyles,
    flexGrow: 0,
    background: palette.backgroundRaised,
})

const ContentContainer = styled('div')({
    display: 'flex',
    position: 'relative',
    containerName: 'content-container',
})

const verticalStyles = css({
    flexFlow: 'row wrap',
    height: 'unset',
    alignContent: 'flex-start',
})

const dropPin = () => {
    // this is so the pin helper can still close itself if a pin is dropped on it
    // without actually pinning anything, no matter where its dropped
    hideDragHelper()
}

const Layout = memo((props: LayoutProps) => {
    const {
        authenticated,
        darkMode,
        // children,
        username,
        isMobile,
        loading,
        level,
        uid,
        ssr,
    } = props

    const {
        connecting: socketConnecting,
        connected: socketConnected,
        paused: socketPaused,
        error: socketError,
    } = useHealthAppState()
    const { ageGateActive } = useSecureState(SecureStateType.AGE_VERIFICATION)
    const maintenanceMode = useAppStateSelector(
        AppStateType.APP,
        'maintenanceMode',
    )
    const modelsLoaded = useModelsLoadedAppState()
    const showSidebar = useSidebarSelector('show')
    const roomExpanded = useFloatingRoomStateSelector('expanded')
    const fullWindow = useLayoutSelector('fullWindow')
    const passiveMode = usePassive()
    const mobileOrTablet = isMobile || isIOS() || isTouchDevice

    const [delayReconnectionScreen, setDelayReconnectionScreen] =
        useState(mobileOrTablet)
    const delayReconnectionScreenTimeout = useRef(null)
    const hasPinnedRooms = usePinnedRoomCount() > 0
    const portrait = useScreenStateSelector('portrait')
    const calculated = useScreenStateSelector('calculated')
    const windowWidth = useScreenStateSelector('windowWidth')
    const contentWidth = useScreenStateSelector('contentWidth')
    const contentHeight = useScreenStateSelector('contentHeight')
    const roomWidth = useScreenStateSelector('roomWidth')
    const roomHeight = useScreenStateSelector('roomHeight')
    const pinsWidth = useScreenStateSelector('pinsWidth')
    const singlePin = useScreenStateSelector('singlePin')
    const pinLimit = useScreenStateSelector('pinLimit')

    const [firstRender, setFirstRender] = useState(ssr)
    useEffect(() => setFirstRender(false), [])
    const shouldHidePins =
        // if we're in a full expanded room
        (passiveMode && roomExpanded) ||
        // or if we're in a room that's portrait (which should behave as expanded)
        (passiveMode && portrait)

    const showBottomNav = portrait && !fullWindow
    const showLeftNav = !portrait && !fullWindow
    const showConnectingScreen =
        (socketConnecting || (!socketConnected && !socketError)) &&
        modelsLoaded &&
        !loading
    const hideRoute = passiveMode && !isTouchDevice

    const usableWidth = portrait
        ? windowWidth
        : shouldHidePins
          ? roomWidth
          : contentWidth

    const BasePage = useRouteSelector('baseComponent')

    useEffect(() => {
        if (!showConnectingScreen || !mobileOrTablet || socketPaused) {
            clearTimeout(delayReconnectionScreenTimeout.current)
            setDelayReconnectionScreen(mobileOrTablet)
            return
        }

        setDelayReconnectionScreen(true)

        delayReconnectionScreenTimeout.current = setTimeout(() => {
            setDelayReconnectionScreen(false)
        }, 5000)

        return () => {
            clearTimeout(delayReconnectionScreenTimeout.current)
        }
    }, [showConnectingScreen, socketPaused])

    if (maintenanceMode || socketError) {
        return <Maintenance />
    }
    if (firstRender) {
        return <div></div>
    }

    return (
        <>
            {loading && (
                <StyledLoadingScreenContainer>
                    <LoadingScreen />
                </StyledLoadingScreenContainer>
            )}
            <Modals
                showConnectingScreen={showConnectingScreen}
                delayReconnectionScreen={delayReconnectionScreen}
            />
            {!fullWindow && (
                <Header
                    authenticated={authenticated}
                    darkMode={darkMode}
                    isMobile={isMobile}
                    level={level}
                    showMenu={!portrait}
                    username={username}
                    uid={uid}
                    vertical={portrait}
                />
            )}
            <StyledDivRoot onDrop={dropPin}>
                {showLeftNav && (
                    <Nav
                        authenticated={authenticated}
                        isMobile={isMobile}
                        vertical={portrait}
                    />
                )}
                {calculated && (
                    <StyledChildrenContainer
                        css={portrait ? verticalStyles : null}
                    >
                        {portrait && (
                            <PinContainer
                                width={windowWidth}
                                vertical={portrait}
                                singlePin={singlePin}
                                pinLimit={pinLimit}
                                hidePins={shouldHidePins}
                            />
                        )}
                        <ContentContainer
                            style={{
                                width: hasPinnedRooms
                                    ? usableWidth
                                    : windowWidth,
                                height: passiveMode
                                    ? roomHeight
                                    : contentHeight,
                                containerType: fullWindow
                                    ? 'normal'
                                    : 'inline-size',
                            }}
                        >
                            {ageGateActive ? (
                                <AgeGateNotice />
                            ) : hideRoute ? null : (
                                <BasePage ssr={ssr} />
                            )}
                        </ContentContainer>
                        {!portrait && (
                            <PinContainer
                                width={pinsWidth}
                                vertical={portrait}
                                singlePin={singlePin}
                                pinLimit={pinLimit}
                                hidePins={shouldHidePins}
                            />
                        )}
                    </StyledChildrenContainer>
                )}
                {!ageGateActive && !fullWindow && showSidebar && (
                    <Sidebar authenticated={authenticated} />
                )}
            </StyledDivRoot>
            {showBottomNav && (
                <Nav
                    authenticated={authenticated}
                    isMobile={isMobile}
                    vertical={portrait}
                />
            )}
            <Floaters isMobile={isMobile} />
        </>
    )
})

export default Layout
